import React from 'react';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import 'symbol-observable';

import App from './App';

if (env.Sentry.dsn !== null) {
    Sentry.init({
        dsn: env.Sentry.dsn,
        integrations: [new Sentry.BrowserTracing()],
        tracesSampleRate: 0,
        environment: env.NODE_ENV,
        // https://gist.github.com/impressiver/5092952
        ignoreErrors: [
            // Random plugins/extensions
            'top.GLOBALS',
            'gtag is not defined',
            "Can't find variable: gtag",
            "Can't find variable: gmo",
            // Wordpress specific
            'wpmobileappStartLoading',
            'wpmobileappStopLoading',
            'Non-Error promise rejection captured', // https://github.com/getsentry/sentry-javascript/issues/3440
            'ResizeObserver loop completed with undelivered notifications', // https://yurplan.atlassian.net/browse/YP-9182
            // Meta specific
            "Can't find variable: _AutofillCallbackHandler", // https://bitbucket.org/yurplan/dev-env/pull-requests/1382/overview
            'Java exception was raised during method invocation',
            'Java object is gone', // Erreur WebView Android dans les navigateurs in-app (Instagram/Facebook)
            "null is not an object (evaluating 'document.querySelector('button[aria-label*=\"search\"]').addEventListener')",
            // WebKit specific (see this issue comment for more details: https://github.com/getsentry/sentry-javascript/issues/3040#issuecomment-913549441)
            "undefined is not an object (evaluating 'window.webkit.messageHandlers[t].postMessage')",
            "undefined is not an object (evaluating 'window.webkit.messageHandlers[e].postMessage')",
            "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
            "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers')",
            "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers[e].postMessage')",
            "Can't find variable: SCDynimacBridge", // Erreur dans le code Natif des applications mobile
            // Third-party tracking/analytics scripts
            'TrackerStorageType is not defined', // Script tracker tiers injecté
            /e\.addEventListener is not a function/i, // Scripts beacon tiers (full-beacon-init)
            // Browser extensions
            'Invalid call to runtime.sendMessage()', // Extensions navigateur (Tab not found)
            // Safari iOS — fetch interrompu quand l'utilisateur quitte la page
            'Load failed',
            // Navigateurs in-app Facebook / WebView anciens — incompatibilités JS engine
            /Invalid regular expression/i,
            /Unexpected (token|identifier|end of input)/i,
            'Invalid or unexpected token',
            // Erreur DOM isolée sans stacktrace exploitable
            'The object does not support the operation or argument.',
            // Google Translate modifie le DOM → React ne peut plus reconcilier (issue React #11538)
            "Failed to execute 'removeChild' on 'Node'",
            "Failed to execute 'insertBefore' on 'Node'",
            'The object can not be found here.', // Variante WebKit (Chrome iOS) du removeChild
            // Navigateurs exotiques (DuckDuckGo, etc.) — erreur CORS sans stacktrace
            'invalid origin',
            // Bots/scanners DOM externes (js_dom_analyser)
            'console.error is not a function',
            // Navigateurs in-app (Ecosia, etc.) — SyntaxError sur parsing JS moderne
            'No identifiers allowed directly after numeric literal',
            // Twitter in-app webview — variable CONFIG non injectée dans le contexte isolé
            "Can't find variable: CONFIG",
            // Extensions navigateur injectant des scripts (user agent switcher, etc.)
            "Failed to execute 'appendChild' on 'Node'",
            // Facebook Pixel non chargé (bloqueurs de pub, navigateurs privés)
            "Can't find variable: fbq",
            // Scripts beacon tiers sur navigateurs anciens (full-beacon-init)
            /replaceAll is not a function/i,
            // Navigateurs in-app Facebook — SyntaxError variantes non couvertes par le regex Unexpected
            /missing \) after argument list/i,
            // Bridge natif tiers (SDK événementiel, etc.) — appels inter-process échoués
            'Error invoking postEvent',
        ],
        denyUrls: [
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Scripts exécutés hors contexte (iframe vide, extensions)
            /^about:blank$/,
            // Chrome Mobile iOS (WKWebView) — stacktrace opaque `undefined:10:73`, pas de code first-party
            /^undefined$/,
        ],
    });
}

const container = document.querySelector('#app');
if (container === null) {
    throw new Error('No container found');
}
const appName = container.getAttribute('data-app-name');
if (appName === null) {
    throw new Error('No application found');
}
const root = createRoot(container);
root.render(<App appName={appName as Configuration.AppName} />);
